import { Typography, Button } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Persisted, Poll } from '@tunasong/schemas'
import { isDatePoll, isManyOfManyPoll, isOneOfManyPoll, isPersisted, isPoll } from '@tunasong/schemas'
import type { FC } from 'react'
import { useState } from 'react'
import invariant from 'tiny-invariant'
import { AddToCalendarDialog } from '../add-to-calendar-dialog.js'
import { DatePollFinal } from '../date-poll-final.js'
import { ManyOfManyPollFinal } from '../many-of-many/final.js'
import { OneOfManyPollFinal } from '../one-of-many/final.js'
import type { PollViewProps } from './types.js'
import styles from './final-option.module.css'

const ResponsesView: FC<PollViewProps> = props => {
  const { entity: poll } = props

  const updateEntity = graphHooks.useEntityUpdate<Poll>({ debounceDelay: 0 })

  const [final, setFinal] = useState<Persisted<Poll>>(poll)
  const [showCalendar, setShowCalendar] = useState(false)

  /** We use a dummy userId for the final option */
  const userId = 'final'

  const handleChange = (updatedPoll: Persisted<Poll>) => {
    setFinal(updatedPoll)
  }
  const handleComplete = () => {
    invariant(isPersisted(final), `handleComplete for non-persisted entity: ${JSON.stringify(final)}`)
    setFinal({ ...final, closed: true })
    updateEntity(final.id, { ...final, closed: true })
  }

  if (!isPoll(final)) {
    return null
  }

  /**
   * For a date poll `finalOption` is a record that may exist but be all-false, so
   * "is it defined" is the wrong question — count the picks instead.
   */
  const pickedCount = isDatePoll(final) ? Object.values(final.finalOption ?? {}).filter(Boolean).length : undefined
  const completeDisabled =
    poll.closed || (pickedCount === undefined ? typeof final.finalOption === 'undefined' : pickedCount === 0)

  const title = poll.closed ? 'Final Results' : 'Finalize Poll'
  return (
    <>
      <Typography variant="h2" gutterBottom>
        {title}
      </Typography>
      <div className={styles.content}>
        {isDatePoll(final) ? <DatePollFinal poll={final} onChange={handleChange} onConfirm={handleComplete} /> : null}
        {!isDatePoll(final) && isManyOfManyPoll(final) ? (
          <ManyOfManyPollFinal poll={final} userId={userId} onChange={handleChange} sort={false} />
        ) : null}
        {isOneOfManyPoll(final) ? (
          <OneOfManyPollFinal poll={final} userId={userId} onChange={handleChange} sort={false} />
        ) : null}
      </div>

      {/* The handoff only exists once the poll is closed and the winners are settled. */}
      {final.closed && isDatePoll(final) ? (
        <>
          <Button variant="outlined" color="secondary" onClick={() => setShowCalendar(true)}>
            Add all to calendar
          </Button>
          <AddToCalendarDialog poll={final} open={showCalendar} onClose={() => setShowCalendar(false)} />
        </>
      ) : null}

      {/* A date poll owns its own confirm, including the fewer-than-needed dialog. */}
      {!final.closed && !isDatePoll(final) ? (
        <Button
          variant="outlined"
          color="secondary"
          disabled={completeDisabled}
          onClick={handleComplete}
          className={styles.completeButton}
        >
          Select final option(s)
        </Button>
      ) : null}
    </>
  )
}

export default ResponsesView
